import { motion } from "framer-motion";
import { useState } from "react";

const practices = [
    { title: "Personal Injury", items: ["Auto call routing and scheduled check-ins", "Data capture and systems sync", "Call guidance and insights"] },
    { title: "Mass Tort", items: ["Screen claimants at scale", "Automated outreach communication", "Retrieve missing information"] },
    { title: "Social Security", items: ["Capture medical and work history", "Provider/adjuster auto-responses", "Follow up for missing records"] },
    { title: "Medical Malpractice", items: ["Capture provider, facility, timeline", "Identify gaps and trigger retrieval", "AI-guided intake questionnaires"] },
    { title: "Workers' Compensation", items: ["Capture injury and filing deadlines", "Manage providers and adjusters", "Outbound follow-up for missing docs"] },
    { title: "Estate Planning", items: ["Pre-qualify by asset and family profile", "Automate wills, trusts, POA workflows", "Reminders for life events and reviews"] },
];

const MOBILE_LIMIT = 3;

const PracticeAreasSection = () => {
    const [showAll, setShowAll] = useState(false);
    return (
        <section id="practice" data-testid="practice-section" className="relative py-24 md:py-32 bg-[var(--paper)] border-t border-[var(--border)]">
            <div className="max-w-[1280px] mx-auto px-6 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="mb-12 md:mb-14 max-w-4xl"
                >
                    <span className="eyebrow-pill">Practice areas</span>
                    <h2 className="font-display text-[clamp(2rem,4.2vw,3.2rem)] leading-[1.04] tracking-tight font-semibold text-[var(--ink)] mt-5">
                        Handcrafted for <span className="accent-blue">your practice.</span>
                    </h2>
                    <p className="mt-5 text-lg text-[var(--ink-soft)]">
                        Every firm uses different intake logic, workflows, and case management fields. We tune every agent to yours — starting with the practice areas we know best.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {practices.map((p, i) => (
                        <motion.div
                            key={p.title}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                            whileHover={{ y: -4 }}
                            data-testid={`practice-card-${i}`}
                            className={`card-paper p-7 group ${
                                i >= MOBILE_LIMIT && !showAll ? "hidden md:block" : ""
                            }`}
                        >
                            <div className="flex items-center gap-3 mb-5">
                                <span className="font-mono text-[10px] accent-coral tracking-wider">0{i + 1}</span>
                                <div className="flex-1 h-px bg-[var(--border)]" />
                            </div>
                            <h3 className="font-display text-2xl text-[var(--ink)] mb-5 leading-tight">{p.title}</h3>
                            <ul className="space-y-3">
                                {p.items.map((item, j) => (
                                    <li key={item} className="flex items-start gap-3 text-[14.5px] text-[var(--ink-soft)] group-hover:text-[var(--ink)] transition-colors">
                                        <i className="fa-solid fa-arrow-right text-[10px] mt-1.5 accent-coral" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    ))}
                </div>

                {/* Mobile-only "Show more" toggle */}
                {!showAll && practices.length > MOBILE_LIMIT && (
                    <div className="mt-8 flex justify-center md:hidden">
                        <button
                            type="button"
                            onClick={() => setShowAll(true)}
                            data-testid="practice-show-more"
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[var(--border-strong)] bg-[var(--paper)] text-[var(--ink)] font-medium text-sm hover:bg-[var(--ink)] hover:text-white hover:border-[var(--ink)] transition-colors"
                        >
                            Show {practices.length - MOBILE_LIMIT} more practice areas
                            <i className="fa-solid fa-arrow-down text-xs" />
                        </button>
                    </div>
                )}

                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.3 }}
                    className="mt-12 text-center"
                >
                    <p className="text-[var(--ink-soft)]">
                        Don&apos;t see your practice area? The Lexafide builder adapts to any legal vertical.{" "}
                        <a
                            href="https://calendly.com/d/d3nd-zhb-2fr/lexafide-demo-discussion-30-minute-meeting-web"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="practice-talk-link"
                            className="accent-coral font-medium link-grow"
                        >
                            Let&apos;s talk →
                        </a>
                    </p>
                </motion.div>
            </div>
        </section>
    );
};

export default PracticeAreasSection;
